/**
 * Shared, live record collections — the data layer behind `source`.
 *
 *   <g-table source="/api/deals" live="deal"></g-table>
 *   <g-kpi   source="/api/deals" live="deal" ...></g-kpi>
 *
 * Every component on a page that names the same endpoint shares ONE
 * collection: one fetch, one set of socket listeners, one array. That is the
 * point. A KPI row and a table over the same entity used to fetch twice and
 * listen twice, and an edit in the table reached the KPIs only by a round
 * trip through the server.
 *
 * The contract is the generated-app API, which every Gainable app shares:
 *
 *   GET  <endpoint>        → { success: true, data: [record, …] }
 *   PUT  <endpoint>/<id>   → { success: true, data: record }   (partial body: $set)
 *   socket `<live>:created` record   `<live>:updated` record   `<live>:deleted` { _id }
 *
 * on `window.appSocket` (boilerplate/public/js/socket-client.js). The socket
 * name is NOT derivable from the endpoint — `/api/purchase-orders` broadcasts
 * as `purchase-order` — so `live` names it, and without it the collection
 * loads once and does not follow changes.
 *
 * Records are replaced, never mutated: every change produces a new array and
 * a new object for the record that changed, so a subscriber can tell what
 * moved by identity.
 *
 * Out of scope for now: server-paged sources (`server-total`) and the
 * `/_meta/stats` aggregation large dashboards use. Both stay host-driven.
 */

// One registry per page, not per bundle. Rollup emits one IIFE per src/
// directory, so g-table and g-kpi each carry a private copy of this module; a
// module-level Map would give each bundle its own collection — two fetches,
// and an edit in the table that the KPIs never hear about.
const REGISTRY = Symbol.for('gainable.collections');

function registry() {
  const g = globalThis;
  if (!g[REGISTRY]) g[REGISTRY] = new Map();
  return g[REGISTRY];
}

const normalize = (endpoint) => String(endpoint || '').trim().replace(/\/+$/, '');

/** Only same-origin paths. An attribute must not be able to point a page's fetch — and its cookies — anywhere else. */
export function isValidEndpoint(endpoint) {
  return typeof endpoint === 'string' && /^\/(?!\/)[\w\-./]*$/.test(endpoint.trim());
}

/** The collection for an endpoint, created on first use. `live` attaches socket updates. */
export function collection(endpoint, { live } = {}) {
  const key = normalize(endpoint);
  if (!isValidEndpoint(key)) throw new Error(`[gainable] invalid source "${endpoint}" — expected a same-origin path like /api/deals`);
  const reg = registry();
  let c = reg.get(key);
  if (!c) { c = new Collection(key); reg.set(key, c); }
  if (live) c.follow(live);
  return c;
}

const idOf = (r) => (r && r._id != null ? String(r._id) : null);

async function readJson(res) {
  try { return await res.json(); } catch { return null; }
}

export class Collection {
  constructor(endpoint) {
    this.endpoint = endpoint;
    this.records = [];
    this.status = 'idle';   // idle | loading | ready | error
    this.error = null;
    this.entity = null;
    this._listeners = new Set();
    this._loading = null;
    this._queue = null;     // socket events that arrive while a load is in flight
    this._socket = null;
    this._handlers = null;
    this._connectedOnce = false;
  }

  /**
   * Called with the collection now and after every change. Starts the first
   * load. Returns the unsubscribe function.
   */
  subscribe(fn) {
    this._listeners.add(fn);
    if (this.status === 'idle') this.load();
    else queueMicrotask(() => { if (this._listeners.has(fn)) this._notifyOne(fn); });
    return () => this._listeners.delete(fn);
  }

  /** Fetch the whole collection. Concurrent calls share one request. */
  load() {
    if (this._loading) return this._loading;
    this.status = 'loading';
    this.error = null;
    this._queue = [];
    this._emit();
    const fetchFn = globalThis.fetch;
    this._loading = (async () => {
      try {
        const res = await fetchFn(this.endpoint, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        const body = await readJson(res);
        if (!res.ok || !body || body.success === false) throw new Error((body && body.error) || `Could not load ${this.endpoint} (${res.status})`);
        this.records = Array.isArray(body.data) ? body.data : [];
        this.status = 'ready';
        // A change broadcast while the GET was in flight may or may not be in
        // the response. Replaying it is safe either way: upsert and remove are
        // idempotent, so a duplicate lands on the record it already describes.
        const queued = this._queue;
        this._queue = null;
        for (const [kind, payload] of queued) this._apply(kind, payload, false);
      } catch (err) {
        this.status = 'error';
        this.error = (err && err.message) || 'Could not load';
        console.error('[gainable] load failed:', this.endpoint, err);
      } finally {
        this._queue = null;
        this._loading = null;
        this._emit();
      }
    })();
    return this._loading;
  }

  /** Resolves once loaded (starting the load if nothing has yet). Never rejects — check `status`. */
  ready() {
    if (this.status === 'idle') return this.load();
    return this._loading || Promise.resolve();
  }

  /** Follow `<entity>:created|updated|deleted` on window.appSocket. */
  follow(entity) {
    const name = String(entity || '').trim();
    if (!name || name === this.entity) return;
    this._detach();
    this.entity = name;
    this._attach();
  }

  get(id) {
    const key = id == null ? null : String(id);
    return this.records.find((r) => idOf(r) === key) || null;
  }

  /**
   * PUT a partial change. Resolves with the saved record (also merged in);
   * rejects with the server's message.
   */
  async save(id, patch) {
    const res = await globalThis.fetch(`${this.endpoint}/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(patch || {}),
    });
    const body = await readJson(res);
    if (!res.ok || !body || body.success === false) throw new Error((body && body.error) || `Could not save (${res.status})`);
    if (body.data && idOf(body.data) != null) this._apply('updated', body.data);
    return body.data;
  }

  /**
   * Optimistic save: every subscriber shows the change now, and it is undone
   * if the server refuses it. Only the patched fields are rolled back, and only
   * where they still hold this edit's value — a newer change to the same
   * field, from this user or a broadcast, is not overwritten by a stale undo.
   */
  async update(id, patch) {
    const before = this.get(id);
    if (before) this._replace(id, { ...before, ...patch });
    try {
      return await this.save(id, patch);
    } catch (err) {
      const cur = this.get(id);
      if (before && cur) {
        const undo = {};
        for (const k of Object.keys(patch)) if (cur[k] === patch[k]) undo[k] = before[k];
        if (Object.keys(undo).length) this._replace(id, { ...cur, ...undo });
      }
      throw err;
    }
  }

  // ── Internals ──

  _replace(id, rec) {
    const key = String(id);
    this.records = this.records.map((r) => (idOf(r) === key ? rec : r));
    this._emit();
  }

  _apply(kind, payload, emit = true) {
    const id = idOf(payload);
    if (id == null) return;
    if (this._queue) { this._queue.push([kind, payload]); return; }
    if (kind === 'deleted') {
      const next = this.records.filter((r) => idOf(r) !== id);
      if (next.length === this.records.length) return;
      this.records = next;
    } else {
      // An update for a record we do not hold is an upsert, not a no-op: the
      // row ACL can make a record visible to this user by changing it.
      const i = this.records.findIndex((r) => idOf(r) === id);
      this.records = i < 0 ? [...this.records, payload] : this.records.map((r, j) => (j === i ? payload : r));
    }
    if (emit) this._emit();
  }

  _attach() {
    const s = globalThis.appSocket;
    if (!s || typeof s.on !== 'function') {
      // socket-client.js loads before the deferred component bundle in a
      // generated app, so this is a page without sockets — or one that loads
      // them later. Try once more when the document is ready.
      if (typeof document !== 'undefined' && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => { if (!this._socket && this.entity) this._attach(); }, { once: true });
      }
      return;
    }
    const e = this.entity;
    this._socket = s;
    this._connectedOnce = !!s.connected;
    this._handlers = {
      [`${e}:created`]: (p) => this._apply('created', p),
      [`${e}:updated`]: (p) => this._apply('updated', p),
      [`${e}:deleted`]: (p) => this._apply('deleted', p),
      // Events broadcast while the socket was down are gone for good. A
      // reconnect therefore reloads, or the page stays quietly out of date.
      connect: () => {
        if (this._connectedOnce && this.status !== 'idle') this.load();
        this._connectedOnce = true;
      },
    };
    for (const [evt, fn] of Object.entries(this._handlers)) s.on(evt, fn);
  }

  _detach() {
    if (this._socket && this._handlers) {
      for (const [evt, fn] of Object.entries(this._handlers)) this._socket.off(evt, fn);
    }
    this._socket = null;
    this._handlers = null;
  }

  _notifyOne(fn) {
    try { fn(this); } catch (err) { console.error('[gainable] collection subscriber threw:', err); }
  }

  _emit() {
    for (const fn of [...this._listeners]) this._notifyOne(fn);
  }
}

/**
 * Element-side glue: the `source` / `live` attributes, read and re-read.
 *
 * Each component keeps one of these and calls `sync()` from connected and
 * attributeChanged callbacks and `stop()` on disconnect. `onChange` receives
 * the collection on every change.
 */
export class SourceBinding {
  constructor(host, onChange) {
    this.host = host;
    this.onChange = onChange;
    this.collection = null;
    this._unsub = null;
    this._key = null;
  }

  get active() { return !!this.collection; }

  sync() {
    const src = this.host.getAttribute('source');
    const live = this.host.getAttribute('live');
    const key = src ? `${normalize(src)}|${live || ''}` : null;
    if (key === this._key && (key === null || this._unsub)) return;
    this.stop();
    this._key = key;
    if (!src) return;
    try {
      this.collection = collection(src, { live });
    } catch (err) {
      console.error(err.message);
      return;
    }
    this._unsub = this.collection.subscribe((c) => this.onChange(c));
  }

  stop() {
    if (this._unsub) this._unsub();
    this._unsub = null;
    this.collection = null;
    this._key = null;
  }
}

// Hosts that need the records too — a page's own filters, a detail drawer —
// read the same collection rather than fetching a second copy.
if (typeof globalThis !== 'undefined' && !globalThis.gainableData) {
  globalThis.gainableData = { collection };
}
